import { 
  Container, 
  Typography, 
  Grid, 
  Box,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Card,
  CardMedia,
  Modal,
  IconButton
} from '@mui/material';
import Hero from '../components/common/Hero';
import { Helmet } from 'react-helmet-async';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HistoryIcon from '@mui/icons-material/History';
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople';
import CloseIcon from '@mui/icons-material/Close';
import { useState } from 'react';

const About = () => {
  const [open, setOpen] = useState(false);
  
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  
  return (
    <>
      <Helmet>
        <title>About Brynleigh Estates | Premier Chelsea, AL Community</title>
        <meta name="description" content="Learn about Brynleigh Estates - our community values, history, and what makes us a premier residential community in Chelsea, Alabama." />
        <link rel="canonical" href="https://brynleighestates.com/about" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://brynleighestates.com/about" />
        <meta property="og:title" content="About Brynleigh Estates | Premier Chelsea, AL Community" />
        <meta property="og:description" content="Learn about Brynleigh Estates - our community values, history, and what makes us a premier residential community in Chelsea, Alabama." />
        <meta property="og:image" content="https://brynleighestates.com/images/brynleigh-estates-social-logo.jpeg" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About Brynleigh Estates | Premier Chelsea, AL Community" />
        <meta name="twitter:description" content="Learn about Brynleigh Estates - our community values, history, and what makes us a premier residential community in Chelsea, Alabama." />
        <meta name="twitter:image" content="https://brynleighestates.com/images/brynleigh-estates-social-logo.jpeg" />
      </Helmet>
      
      <Hero 
        imageUrl="/images/hills-bg.jpeg"
        title="About Brynleigh Estates"
        subtitle="A place to call home in Chelsea, Alabama"
      />
      
      <Container maxWidth="lg">
        <Box sx={{ my: 6 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <Typography variant="h4" gutterBottom fontWeight="medium" component="h1">
                Our Community
              </Typography>
              
              <Typography variant="body1" paragraph>
                Brynleigh Estates is a premier residential community located in Chelsea, Alabama. Our neighborhood is known for its beautiful homes, friendly neighbors, and strong sense of community.
              </Typography>
              
              <Typography variant="body1" paragraph>
                Since its establishment, Brynleigh Estates has grown into a tight-knit community where neighbors look out for one another, children play safely, and families thrive. Our homeowners association works diligently to maintain the high standards that make our neighborhood such a desirable place to live.
              </Typography>
              
              <Box sx={{ my: 4 }}>
                <Typography variant="h5" gutterBottom component="h2">
                  Community Values
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <LocationOnIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Location" 
                      secondary="Conveniently located in Chelsea with easy access to schools, shopping, and outdoor recreation." 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <HistoryIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="History" 
                      secondary="Established as a premier development designed to offer quality living in a peaceful setting." 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <EmojiPeopleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Community Involvement" 
                      secondary="Active homeowners who participate in community events and neighborhood improvements." 
                    />
                  </ListItem>
                </List>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card 
                sx={{ 
                  mb: 3, 
                  cursor: 'pointer',
                  transition: 'transform 0.3s',
                  '&:hover': {
                    transform: 'scale(1.02)',
                  }
                }}
                onClick={handleOpen}
              >
                <CardMedia
                  component="img"
                  image="/images/chelsea.jpeg"
                  alt="Chelsea, Alabama"
                  sx={{ 
                    height: 280,
                    objectFit: 'cover',
                    objectPosition: 'center bottom'
                  }}
                />
                <Box sx={{ p: 1, bgcolor: 'background.paper', textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Click to view full image
                  </Typography>
                </Box>
              </Card>
              
              <Paper elevation={0} sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h5" gutterBottom fontWeight="medium" component="h2">
                  Neighborhood Facts
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">Location</Typography>
                  <Typography variant="body2">Chelsea, Alabama</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">School District</Typography>
                  <Typography variant="body2">Shelby County Schools</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">Community Features</Typography>
                  <Typography variant="body2">Well-maintained common areas, community events, and friendly neighbors</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">HOA Benefits</Typography>
                  <Typography variant="body2">Property value protection, community standards, and neighborhood representation</Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Container>
      
      {/* Modal for full-size image */}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="chelsea-image-modal"
        aria-describedby="full-size-image-of-chelsea"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2
        }}
      >
        <Box sx={{ 
          position: 'relative',
          maxWidth: '95vw',
          maxHeight: '95vh',
          outline: 'none'
        }}>
          <IconButton
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(0,0,0,0.5)',
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(0,0,0,0.7)',
              }
            }}
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
          <img 
            src="/images/chelsea.jpeg" 
            alt="Chelsea, Alabama - Full Size" 
            style={{ 
              maxWidth: '100%', 
              maxHeight: '95vh', 
              display: 'block',
              boxShadow: '0 8px 16px rgba(0,0,0,0.2)'
            }} 
          />
        </Box>
      </Modal>
    </>
  );
};

export default About; 